import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { topicsApi, projectsApi, documentationApi } from "../../../services/api"
import Button from "../../../components/ui/Button"
import Card, { CardBody, CardHeader } from "../../../components/ui/Card"
import { useTopicGenerationStatus } from "./useTopicGenerationStatus"

const Topic = ({ setPage, topicId, topicPreview, backPage = "SavedIdeas", onGenerateDocFromTopic }) => {
  const [topic, setTopic] = useState(topicPreview || null)
  const [loading, setLoading] = useState(!!topicId && !topicPreview)
  const [projects, setProjects] = useState([])
  const [documentations, setDocumentations] = useState([])

  const { shouldHideGenerate, getTopicStatusLabel, getProjectLink } = useTopicGenerationStatus(projects, documentations)

  useEffect(() => {
    projectsApi.list({}).then((res) => {
      if (res?.data?.data?.projects) setProjects(res.data.data.projects)
    }).catch(() => {})
    documentationApi.list({}).then((res) => {
      if (res?.data?.data) setDocumentations(res.data.data)
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (topicPreview) {
      setTopic(topicPreview)
      setLoading(false)
      return
    }
    if (!topicId) {
      setTopic(null)
      setLoading(false)
      return
    }
    setLoading(true)
    topicsApi.get(topicId)
      .then(res => {
        if (res?.data?.data) setTopic(res.data.data)
      })
      .catch(() => setTopic(null))
      .finally(() => setLoading(false))
  }, [topicId, topicPreview])

  const backLabel = backPage === "TopicIdeas" ? "Back to generated topics" : "Back to ideas"

  if (loading) return <div className="p-4 text-slate-600 animate-soft-pulse">Loading...</div>
  if (!topic && topicId) return <div className="p-4">Topic not found. <button className="link" onClick={() => setPage(backPage)}>{backLabel}</button></div>
  if (!topic) return (
    <div className="p-4">
      <p className="text-slate-600">Select a topic from Saved Ideas.</p>
      <Button size="sm" className="mt-2" onClick={() => setPage(backPage)}>{backLabel}</Button>
    </div>
  )

  const hasDetails = topic.summary || (topic.features && topic.features.length) || topic.explanation
  const statusLabel = getTopicStatusLabel(topic._id)
  const projectLinkId = getProjectLink(topic._id)

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-start">
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="ghost" size="sm" onClick={() => setPage(backPage)}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75 3 12m0 0 3.75-3.75M3 12h18" />
            </svg>
          </Button>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{topic.title}</h2>
          {onGenerateDocFromTopic && topic._id && !shouldHideGenerate(topic._id) ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onGenerateDocFromTopic(topic._id, topic)}
            >
              Generate project documentation
            </Button>
          ) : null}
          {statusLabel && projectLinkId ? (
            <Link
              to={`/dashboard/project?projectId=${encodeURIComponent(String(projectLinkId))}`}
              className="text-sm font-semibold text-emerald-700 hover:text-emerald-800"
            >
              {statusLabel} →
            </Link>
          ) : null}
        </div>
      </div>
      <div className="mt-6 flex flex-col gap-4">
        {topic.summary && (
          <Card className="shadow-soft">
            <CardHeader title="Summary" />
            <CardBody className="pt-2">
              <p className="whitespace-pre-wrap text-slate-800 leading-relaxed">{topic.summary}</p>
            </CardBody>
          </Card>
        )}
        {topic.features && topic.features.length > 0 && (
          <Card className="shadow-soft">
            <CardHeader title="Features" />
            <CardBody className="pt-2">
              <ul className="list-disc list-inside space-y-1.5 text-slate-800">
                {topic.features.map((f, i) => (
                  <li key={i} className="leading-relaxed">{f}</li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}
        {topic.explanation && (
          <Card className="shadow-soft">
            <CardHeader title="Explanation" />
            <CardBody className="pt-2">
              <p className="whitespace-pre-wrap text-slate-800 leading-relaxed">{topic.explanation}</p>
            </CardBody>
          </Card>
        )}
        {!hasDetails && (
          <Card className="shadow-soft">
            <CardBody>
              <p className="whitespace-pre-wrap text-slate-800 leading-relaxed">{topic.description}</p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  )
}

export default Topic
